import React, { useEffect, useState } from "react";
import { Card, Space, Input, message, Spin } from "antd";
import { DownloadOutlined, FolderViewOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_URL } from "../../config";

const DocumentsTab = React.memo(function DocumentsTab(props) {
  const { patient_id, patientDetails } = props;
  const [documents, setDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState({});

  const appointmentFiles = patientDetails.appointment_files || [];

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/document/${patient_id}/`,
        );
        setDocuments(response.data);
      } catch (error) {
        console.error("Error fetching documents:", error);
        message.error("Failed to fetch documents.");
      }
    };

    fetchDocuments();
  }, [API_URL, patient_id]);

  const handleDownload = async (document_id, fileName) => {
    try {
      setLoading((prevLoading) => ({ ...prevLoading, [document_id]: true }));
      const response = await axios.get(
        `${API_URL}/document/?document_id=${document_id}`,
        {
          responseType: "blob",
        },
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      message.success("Download successful.");
    } catch (error) {
      console.error("Error downloading document:", error);
      message.error("Failed to download document. Please try again.");
    } finally {
      setLoading((prevLoading) => ({ ...prevLoading, [document_id]: false }));
    }
  };

  const handleView = (document_id) => {
    window.open(`${API_URL}/document?document_id=${document_id}`, "_blank");
  };

  const groupedDocuments = documents.reduce((acc, doc) => {
    const { category } = doc;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(doc);
    return acc;
  }, {});

  const filteredDocuments = Object.keys(groupedDocuments).reduce(
    (acc, category) => {
      const filtered = groupedDocuments[category].filter((doc) =>
        doc.filename.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      if (filtered.length) {
        acc[category] = filtered;
      }
      return acc;
    },
    {},
  );

  const sortedCategories = Object.keys(filteredDocuments).sort((a, b) => {
    if (a === "APPOINTMENT_FILES") return -1;
    if (b === "APPOINTMENT_FILES") return 1;
    return a.localeCompare(b);
  });

  const formatCategoryTitle = (category) => {
    return category.replace(/_/g, " ");
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Input.Search
        placeholder="Search by filename"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      {sortedCategories.map((category) => (
        <Card key={category} title={formatCategoryTitle(category)}>
          {filteredDocuments[category]
            .filter(
              (doc) =>
                category !== "APPOINTMENT_FILES" ||
                appointmentFiles.includes(doc.document_id),
            )
            .map((doc) => (
              <Space
                key={doc.document_id}
                direction="horizontal"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  marginBottom: 10,
                }}
              >
                <span style={{ marginTop: 8 }}>{doc.filename}</span>
                <Space>
                  <FolderViewOutlined
                    title="Preview"
                    style={{ fontSize: 23, color: "#f01633" }}
                    onClick={() => handleView(doc.document_id)}
                  />
                  <div
                    style={{ position: "relative", display: "inline-block" }}
                  >
                    <Spin
                      spinning={loading[doc.document_id] || false}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <DownloadOutlined
                        title="Download"
                        style={{ fontSize: 23, color: "#3043f0" }}
                        onClick={() =>
                          handleDownload(doc.document_id, doc.filename)
                        }
                      />
                    </Spin>
                  </div>
                </Space>
              </Space>
            ))}
        </Card>
      ))}
    </Space>
  );
});

export default DocumentsTab;
